import { defineStore } from 'pinia';
import type { BreadCrumbMap, Breadcrumb, BreadcrumbErrorFetch } from '@/types/page';
import type { HttpResponse } from '@/utils/HttpClient';

export const useBreadcrumbStore = defineStore('breadcrumb', () => {
  const breadcrumbsMap = ref<BreadCrumbMap>({});

  const promises: { [key: string]: Promise<HttpResponse<Breadcrumb, BreadcrumbErrorFetch>> } = {};
  const getBreadcrumb = async ({
    slug,
    name,
    assortmentId,
  }: {
    slug: string;
    name?: string;
    assortmentId?: string;
  }) => {
    let data: Breadcrumb;
    const assortment = assortmentId || 'default';
    const existingPromise = promises[slug];
    if (breadcrumbsMap.value?.[assortment]?.[slug]) {
      data = { ...breadcrumbsMap.value[assortment][slug] };
    } else if (existingPromise) {
      const resolved = await existingPromise;
      data = { name: undefined, parents: [], ...resolved.data };
    } else {
      const { $productService } = useNuxtApp();
      const promise = $productService.fetchBreadcrumbs(slug, undefined, assortment);
      promises[slug] = promise;
      const resolved = await promise;
      data = { name: undefined, parents: [], ...resolved.data };

      if (!breadcrumbsMap.value[assortment]) {
        breadcrumbsMap.value[assortment] = {};
      }
      breadcrumbsMap.value[assortment][slug] = data;

      delete promises[slug];
    }

    if (!data.parents?.length) {
      // Top most categories does not have a parent array...
      data.parents = [];
    }

    if (name) {
      data = { ...data, parents: [...data.parents] };
      // Copy root element of breadcrumb into parent array
      data.parents?.push({
        name: data.name,
        slug: data.slug,
        parents: [],
      });

      // Set root element name to argument and delete slug
      data.name = name;
      data.slug = undefined;
    }

    return data;
  };

  return {
    getBreadcrumb,
    breadcrumbsMap,
  };
});
